import type { StepCondition } from '@/lib/funnelConditions';
import type { PlanRole } from '@/lib/personalPlan';
import type { OfferStepConfig } from '@/lib/funnelOffer';
import type { ReinforceScreen } from '@/lib/funnelReinforce';
import { detectContactType, normalizePhone } from '@/lib/phone';

/** What a funnel answer becomes in the mailing list — see funnelLeadService. */
export type LeadTarget = 'email' | 'phone' | 'first_name' | 'last_name' | 'custom';

export interface LeadFieldMapping {
  target: LeadTarget;
  /** target==='custom': Smoove contact-field id (e.g. "i4"). */
  smooveFieldId?: string;
  /** target==='custom': key in the outgoing webhook payload, /^[a-z][a-z0-9_]{0,39}$/. */
  webhookKey?: string;
}

export interface OnboardingStepField {
  id: string;
  type: 'text' | 'number' | 'select' | 'multiselect' | 'file' | 'textarea' | 'date';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    minSelections?: number;
    maxSelections?: number;
  };
}

export interface OnboardingStepFeature {
  icon: string;
  title: string;
  description: string;
}

export interface ProfileFieldMapping {
  fieldName: string;        // שם השדה בפרופיל (קיים או חדש)
  fieldLabel?: string;      // תווית תצוגה בעברית (לשדות חדשים)
  isNewField?: boolean;     // האם זה שדה חדש שצריך ליצור
  fieldType?: 'text' | 'number' | 'date' | 'array';  // סוג השדה
}

// רשימת שדות פרופיל זמינים למיפוי
export const PROFILE_FIELDS = [
  { value: 'display_name', label: 'שם תצוגה', type: 'text' },
  { value: 'age', label: 'גיל', type: 'number' },
  { value: 'location', label: 'מיקום', type: 'text' },
  { value: 'about_me', label: 'אודות', type: 'text' },
  { value: 'hobbies', label: 'תחביבים', type: 'text' },
  { value: 'pronouns', label: 'כינויים', type: 'text' },
  { value: 'bio', label: 'ביו', type: 'text' },
  { value: 'headline', label: 'כותרת', type: 'text' },
  { value: 'phone', label: 'טלפון', type: 'text' },
  { value: 'preferred_video_time', label: 'שעת צפייה מועדפת', type: 'text' },
] as const;

/** Where an `info` step's image sits relative to its body text (`questionText`). */
export type ImagePosition = 'above' | 'below';

/**
 * The slot an info-step image takes when `imagePosition` is absent. One
 * constant, read by InfoScreen, `stepCopy` and the edit dialogs, so the three
 * cannot disagree about what "absent" means.
 */
export const DEFAULT_IMAGE_POSITION: ImagePosition = 'above';

export interface OnboardingStep {
  id: string;
  /**
   * `metric-goals` is the one member of this union that is NEVER stored.
   * `withMetricGoalsStep` appends it in code on every render of the wizard —
   * see that function for why it is not a row in `onboarding_config` — and
   * `StepTypeSelector` does not offer it, so no admin can create, reorder or
   * delete one. It is listed here only so the synthetic step is a real
   * `OnboardingStep` rather than something wearing a borrowed type.
   */
  type: 'single-select' | 'multi-select' | 'text-input' | 'date-picker' | 'time-picker' | 'video' | 'info' | 'complete' | 'offer' | 'diagnosis' | 'metric-goals';
  /**
   * Copy for an `offer` step: a full-screen pitch placed inside the quiz.
   *
   * FUNNEL-ONLY, like `showIf`. Onboarding has nothing to sell to someone who is
   * already a member, and `DynamicOnboardingStep` deliberately degrades an offer
   * step to a plain info screen rather than rendering a paywall pitch to one.
   *
   * Type-only import; `funnelOffer` imports nothing from here.
   */
  offer?: OfferStepConfig;
  /**
   * Screens shown BETWEEN this question and the next, chosen by the answer just
   * given — a testimonial, a statistic, a reassurance.
   *
   * FUNNEL-ONLY, like `showIf` and `offer`. Unlike a step with a `showIf`, these
   * are not counted in the progress dots: they are content she is given, not a
   * question she is asked, and counting them is what makes a progress bar appear
   * to grow the more you answer. See `funnelReinforce`.
   */
  reinforce?: ReinforceScreen[];
  /**
   * Answer-routed visibility. Absent means always shown, which is every step that
   * existed before this field, so no stored config needed migrating.
   *
   * Only the FUNNEL honours this today (`visibleSteps` in FunnelStep); onboarding
   * renders its own list and ignores it. The field lives here rather than in a
   * funnel-only type because `funnel_config.steps` deliberately shares this exact
   * shape so one set of admin dialogs can edit both.
   *
   * Type-only import, so this does not create a runtime cycle with funnelConditions.
   */
  showIf?: StepCondition;
  /**
   * Send this answer to the mailing list (Smoove / webhook) as a lead field.
   *
   * FUNNEL-ONLY, like `showIf`. The server maps it against the active config,
   * so a device cannot write arbitrary fields. For a text-input it also decides
   * the input kind (email / tel / name box). Older store builds ignore it.
   */
  leadField?: LeadFieldMapping;
  /**
   * What this answer contributes to the personal plan built at signup.
   *
   * FUNNEL-ONLY. Type-only import, like `showIf`. Older store builds ignore it.
   */
  planRole?: PlanRole;
  title: string;
  questionText?: string;
  description?: string;
  icon?: string;
  field?: OnboardingStepField;
  options?: {
    id: string;
    label: string;
    value: string;
    /**
     * End the quiz here and go straight to this screen.
     *
     * FUNNEL-ONLY, like `showIf`. Restricted to the two terminal screens on
     * purpose — see `funnelRouting` for why a jump to another question would
     * break the progress dots, back navigation and the coverage census at once.
     * Onboarding ignores it; there is nothing to skip to.
     */
    goTo?: "diagnosis" | "paywall";
    /**
     * Multi-select only: this answer cannot be combined with another ("אף אחד
     * מאלה"). Choosing it clears the rest; choosing another clears it.
     */
    exclusive?: boolean;
  }[];
  features?: OnboardingStepFeature[];
  completionMessage?: string;
  tips?: string[];
  videoUrl?: string;
  imageUrl?: string;
  imageCaption?: string;
  /**
   * Where an `info` step's image sits relative to the body text. Absent means
   * `DEFAULT_IMAGE_POSITION`, which is every step stored before this field
   * existed, so no config needed migrating. Read by InfoScreen (through
   * DynamicOnboardingStep) and mirrored by `stepCopy`. A `video` step never
   * reads it: its layout belongs to MediaWelcomeScreen and is fixed.
   */
  imagePosition?: ImagePosition;
  textInputConfig?: {
    placeholder?: string;
    minLength?: number;
    maxLength?: number;
  };
  datePickerConfig?: {
    placeholder?: string;
    minDate?: string;
    maxDate?: string;
  };
  minSelections?: number;
  maxSelections?: number;
  skipButton?: {
    show: boolean;
    text: string;
  };
  continueButton?: {
    show: boolean;
    text: string;
  };
  /**
   * Funnel only, and only while this step is the first screen: the labels of
   * its two buttons. Blank or absent → built-in. See `entryButtonLabels`.
   */
  entryButtons?: {
    start?: string;
    existing?: string;
  };
  order: number;
  isActive: boolean;
  isMandatory: boolean;
  showProgressBar: boolean;
  profileFieldMapping?: ProfileFieldMapping;  // מיפוי לשדה פרופיל
}

export interface OnboardingConfig {
  steps: OnboardingStep[];
  brandName: string;
  brandLogo?: string;
  brandLogoSvg?: string;
  totalSteps: number;
}

// פונקציות עזר
export const getActiveSteps = (config: OnboardingConfig): OnboardingStep[] => {
  return config.steps
    .filter(step => step.isActive)
    .sort((a, b) => a.order - b.order);
};

export const getStepByOrder = (config: OnboardingConfig, order: number): OnboardingStep | undefined => {
  return getActiveSteps(config).find(step => step.order === order);
};

export const getTotalActiveSteps = (config: OnboardingConfig): number => {
  return getActiveSteps(config).length;
};

export const getStepProgress = (config: OnboardingConfig, currentStep: number): number => {
  const totalSteps = getTotalActiveSteps(config);
  return totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;
};

export const METRIC_GOALS_STEP_ID = 'metric-goals';

/**
 * The goal-setting screen, appended to the wizard IN CODE.
 *
 * It is not a row in `onboarding_config` on purpose. That table is a single
 * JSON blob edited live by the admin dialogs, with no migration path and no
 * guard against deleting a step; a screen whose job is to stop new users
 * inheriting an uncalibrated default must not be one careless drag away from
 * vanishing. Appending it here also means it needs no production write to ship,
 * and it can never fall out of sync with the component that renders it.
 *
 * `isMandatory: false` is what puts the דלג button on the screen, and skipping
 * it writes nothing at all — see `planGoalWrites`.
 */
export const buildMetricGoalsStep = (order: number): OnboardingStep => ({
  id: METRIC_GOALS_STEP_ID,
  type: 'metric-goals',
  title: 'היעדים היומיים שלך',
  order,
  isActive: true,
  isMandatory: false,
  showProgressBar: true,
  // No profileFieldMapping, deliberately. The goals live in
  // `user_metric_prefs`, not on the profile, and the wizard's resume logic
  // ("first step with a mapping and no answer") must keep ignoring this step
  // exactly as it ignores the intro videos — otherwise a user who skipped it
  // would be sent back here on every reopen.
});

/**
 * Appends the goal step to the active steps.
 *
 * Empty in, empty out: `OnboardingStep.tsx` reads an empty list as "no config,
 * skip onboarding entirely", and a lone goal screen is not an onboarding flow.
 * Idempotent, so a double call cannot produce two of them.
 */
export const withMetricGoalsStep = (steps: OnboardingStep[]): OnboardingStep[] => {
  if (steps.length === 0) return steps;
  if (steps.some(step => step.id === METRIC_GOALS_STEP_ID)) return steps;
  const lastOrder = steps[steps.length - 1].order ?? steps.length - 1;
  return [...steps, buildMetricGoalsStep(lastOrder + 1)];
};

// Which medium a media ('video') step should render.
// Video wins if set & non-empty, else image if set, else none.
export const getStepMediaSlot = (step: OnboardingStep): 'video' | 'image' | 'none' =>
  step.videoUrl?.trim() ? 'video' : step.imageUrl?.trim() ? 'image' : 'none';

/**
 * The image half of an `info` step's edit form, and the ONE rule for writing
 * it back. Lives here, not in the dialogs, because they render through a Radix
 * Portal that is `null` under the node test suite — the rule is testable only
 * as a function.
 *
 * A blank url clears all three fields: a stored step must never carry a
 * caption or a position without an image, or a later upload would inherit
 * stale copy. `undefined` is dropped by JSON.stringify, so a step that never
 * had an image is stored exactly as before.
 */
export interface InfoImageForm {
  imageUrl: string;
  imageCaption: string;
  imagePosition: ImagePosition;
}

export const readInfoImageForm = (
  step: Pick<OnboardingStep, 'imageUrl' | 'imageCaption' | 'imagePosition'>
): InfoImageForm => ({
  imageUrl: step.imageUrl ?? '',
  imageCaption: step.imageCaption ?? '',
  imagePosition: step.imagePosition ?? DEFAULT_IMAGE_POSITION,
});

export const writeInfoImage = <T extends Pick<OnboardingStep, 'imageUrl' | 'imageCaption' | 'imagePosition'>>(
  step: T,
  form: InfoImageForm
): T => {
  const url = form.imageUrl.trim();
  if (!url) {
    return { ...step, imageUrl: undefined, imageCaption: undefined, imagePosition: undefined };
  }
  return {
    ...step,
    imageUrl: url,
    imageCaption: form.imageCaption.trim() || undefined,
    imagePosition: form.imagePosition,
  };
};

export const blankInfoImageForm = (): InfoImageForm => ({
  imageUrl: '',
  imageCaption: '',
  imagePosition: DEFAULT_IMAGE_POSITION,
});

/**
 * The media half of the create form after the admin changes the step type.
 *
 * An image uploaded for a video screen must not become an info screen's image
 * because the admin pressed חזור and changed their mind: that button is only
 * `setStep(1)`, so every field survives it. Re-picking the SAME type keeps the
 * work — that is a no-op, not a change of mind.
 *
 * Found by the Codex plan review, 2026-09-22, before the info branch started
 * reading `imageUrl` and turned a harmless leak into a published image.
 */
export const imageFormOnTypeChange = (
  prevType: OnboardingStep['type'] | undefined,
  nextType: OnboardingStep['type'],
  current: InfoImageForm
): InfoImageForm => (prevType === nextType ? current : blankInfoImageForm());

/**
 * What a `text-input` step is actually collecting.
 *
 * Needed because an email box and a name box want opposite things: an email is
 * LTR, wants the `@` keyboard, and is worthless unless it is a real address; a
 * Hebrew name is RTL and any non-empty string is valid.
 *
 * It reads three places for the answer because the stored data does not match
 * the declared type. `OnboardingStep.field` is typed `OnboardingStepField` (an
 * object with an `id`), but the live funnel rows hold a bare STRING —
 * `{"id":"email","type":"text-input","field":"email"}` — so `step.field?.id` is
 * `undefined` at runtime for exactly the steps this needs to recognise.
 * Narrowing through `unknown` rather than trusting the annotation is the honest
 * reading of that data; fixing the rows instead would leave every already-saved
 * config broken, and a native binary outlives the row it reads.
 */
export const getTextInputKind = (step: OnboardingStep): 'email' | 'name' | 'phone' | 'text' => {
  // An explicit lead mapping is the admin saying what the box collects; it wins
  // over guessing from ids, which admin-created steps (`step_<ms>`) never match.
  switch (step.leadField?.target) {
    case 'email': return 'email';
    case 'phone': return 'phone';
    case 'first_name':
    case 'last_name': return 'name';
  }
  const raw = step.field as unknown;
  const fieldName =
    typeof raw === 'string'
      ? raw
      : raw && typeof raw === 'object' && typeof (raw as { id?: unknown }).id === 'string'
        ? (raw as { id: string }).id
        : '';

  const hay = `${step.id} ${fieldName}`.toLowerCase();
  if (/\bemail\b|mail/.test(hay)) return 'email';
  if (/name/.test(hay)) return 'name';
  return 'text';
};

/**
 * Deliberately permissive: one `@`, a dot in the domain, no spaces.
 *
 * A stricter pattern rejects real addresses (plus-tags, long TLDs, apostrophes),
 * and on a lead-capture screen a false rejection costs the whole lead while a
 * false accept costs one undeliverable row. The failure this exists to stop is
 * the blunt one measured 2026-08-30, where "not-an-email" passed a length-only
 * check and became the address the plan was promised to.
 */
export const isPlausibleEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

/**
 * Strips bidi control marks (LRM/RLM, the LRE/RLE/PDF/LRO/RLO block, the
 * LRI/RLI/FSI/PDI block — Unicode category Cf) and normalises the unicode
 * hyphen/dash look-alikes and the non-breaking space to their ASCII
 * equivalents, then trims.
 *
 * Written with explicit unicode escapes rather than the literal characters
 * — a source file is the wrong place for invisible bidi-control bytes to
 * live unescaped.
 *
 * iOS autofill / a Contacts paste on a Hebrew-locale phone commonly wraps the
 * number in exactly these marks (e.g. "\u202A+972 50\u2011123\u20114567\u202C"),
 * which otherwise makes a fully valid number fail `isPlausiblePhone` with no
 * visible cause. `src/lib/phone.ts` must stay a byte-for-byte mirror of the
 * edge function's normalizer, so this cleaning happens here, in the funnel
 * caller, and never inside `normalizePhone` itself.
 */
export const cleanPhoneInput = (v: string): string =>
  v
    .replace(/\p{Cf}/gu, '')
    .replace(/[\u2010\u2011\u2012\u2013\u2212]/g, '-')
    .replace(/\u00A0/g, ' ')
    .trim();

/**
 * The funnel's phone gate.
 *
 * `normalizePhone` (the server's rule, and what `getTextInputKind`'s sibling
 * checks used to gate on) also accepts a mobile number one digit short
 * ("050123456") and landlines ("021234567") as "valid" — exactly the failure
 * mode that would let a consenting lead be stored with a number no SMS or
 * WhatsApp message can ever reach. `detectContactType` (the same rule the
 * login screen uses) closes most of that, but not all: a `+972`-prefixed
 * input still falls into `detectContactType`'s generic E.164 branch, which
 * matches ANY plausibly-shaped international number, including a one-digit-
 * short IL mobile or an IL landline. So for an input that is clearly IL
 * (starts with "+972", "972", "0", or a bare "5" — a mobile with the leading
 * 0 dropped, which `IL_MOBILE_REGEX`'s OPTIONAL prefix also accepts on its
 * own), this additionally requires `normalizePhone` to produce a real IL
 * MOBILE — not just any IL-shaped number.
 *
 * The `normalizePhone(clean) !== null` check up front is the actual contract:
 * the server stores exactly `normalizePhone(text)` (see
 * `supabase/functions/_shared/funnelLead.ts`), so the client must never call
 * a value "plausible" that the server would store as `null` — that is a
 * consented lead silently losing its only contact channel. Everything past
 * that gate is refining WHICH normalizable numbers count as a phone here
 * (`detectContactType` for anything not IL-shaped, the stricter mobile-only
 * rule for anything that is).
 */
export const isPlausiblePhone = (v: string): boolean => {
  const clean = cleanPhoneInput(v);
  const normalized = normalizePhone(clean);
  if (normalized === null) return false;
  if (/^(\+972|972|0|5)/.test(clean)) {
    return /^\+9725\d{8}$/.test(normalized);
  }
  return detectContactType(clean) === 'phone';
};

/**
 * When to SHOW the phone-format error, as distinct from whether the number
 * is valid (`isPlausiblePhone`).
 *
 * A flat digit-count floor (the previous "9 digits" gate) fires mid-type: a
 * 10-digit IL mobile passes through a 9-digit prefix on its way to being
 * valid, and a `+972` one passes through 10 and 11. Showing the error there
 * reads as the form arguing with her before she's finished typing — the same
 * problem `showEmailError`'s length gate exists to avoid. This waits for as
 * many digits as a COMPLETE number in the format she appears to be typing
 * would have: 10 for a local "0…" number, 12 for a "+972"/"972" one, 11 for
 * any other "+"-prefixed international number, and 9 for a bare "5…" — a
 * dropped-0 mobile, whose complete (if wrong) length is exactly "5" plus 8
 * digits. That last one can never become valid by typing more, but the
 * message is what tells her to add the 0, so it still waits for the full 9
 * rather than firing on the first keystroke.
 */
export const shouldShowPhoneError = (v: string): boolean => {
  if (isPlausiblePhone(v)) return false;
  const cleaned = cleanPhoneInput(v);
  const digitCount = cleaned.replace(/\D/g, '').length;
  const threshold = cleaned.startsWith('0')
    ? 10
    : /^(\+972|972)/.test(cleaned)
      ? 12
      : cleaned.startsWith('+')
        ? 11
        : cleaned.startsWith('5')
          ? 9
          : 10;
  return digitCount >= threshold;
};
